/**
 * ReasonRater — Rate the ML StrategyAnalyzer's reasons for its card choice.
 *
 * Displays the main reason and secondary reasons with:
 * - Category badge (ENTAME, SIGNAL, BELIEF, TACTICAL, etc.)
 * - Reason text
 * - Three-button rating: Pertinent (1) / Neutre (0) / Non pertinent (-1)
 * - Optional correction text field
 *
 * Also rates individual signals and beliefs used by the model.
 */

import { useState, useCallback } from 'react';
import { substitutePlayerNames } from '../utils/cards';

// ============================================
// Types
// ============================================

interface Reason {
  categorie: string;
  texte: string;
  importance?: number;
}

export interface ReasonRatingResult {
  reason_category: string;
  reason_text: string;
  is_main_reason: boolean;
  rating: number;
  expert_correction?: string;
}

export interface SignalRatingResult {
  signal_type: 'signal' | 'belief';
  signal_text: string;
  rating: number; // 1 = correct, -1 = wrong
}

interface ReasonRaterProps {
  strategy: {
    raison_principale?: Reason;
    raisons_secondaires?: Reason[];
    risques?: string[];
    signaux_utilises?: string[];
    beliefs_utilises?: string[];
  } | null;
  onChange: (ratings: ReasonRatingResult[]) => void;
  onSignalChange?: (ratings: SignalRatingResult[]) => void;
  taker?: number | null;
  evaluatedSeat?: number;
  viewPosition?: number;
}

// ============================================
// Single reason row
// ============================================

function ReasonRow({
  reason,
  isMain,
  rating,
  correction,
  onRatingChange,
  onCorrectionChange,
}: {
  reason: Reason;
  isMain: boolean;
  rating: number | null;
  correction: string;
  onRatingChange: (value: number) => void;
  onCorrectionChange: (value: string) => void;
}) {
  const [showCorrection, setShowCorrection] = useState(false);

  return (
    <div className={`reason-row ${isMain ? 'reason-main' : ''}`}>
      <div className="reason-header">
        <span className={`reason-badge reason-badge-${reason.categorie.toLowerCase()}`}>
          {reason.categorie}
        </span>
        {isMain && <span className="reason-main-label">principale</span>}
      </div>
      <div className="reason-text">{reason.texte}</div>
      <div className="reason-buttons">
        <button
          className={`reason-btn reason-btn-good ${rating === 1 ? 'active' : ''}`}
          onClick={() => onRatingChange(1)}
          title="Pertinent"
        >
          ✓
        </button>
        <button
          className={`reason-btn reason-btn-neutral ${rating === 0 ? 'active' : ''}`}
          onClick={() => onRatingChange(0)}
          title="Neutre"
        >
          —
        </button>
        <button
          className={`reason-btn reason-btn-bad ${rating === -1 ? 'active' : ''}`}
          onClick={() => onRatingChange(-1)}
          title="Non pertinent"
        >
          ✕
        </button>
        <button
          className="reason-btn reason-btn-correction"
          onClick={() => setShowCorrection(prev => !prev)}
          title="Ajouter une correction"
        >
          ✎
        </button>
      </div>
      {showCorrection && (
        <input
          className="reason-correction-input"
          type="text"
          placeholder="Ce que la raison aurait dû dire..."
          value={correction}
          onChange={e => onCorrectionChange(e.target.value)}
        />
      )}
    </div>
  );
}

// ============================================
// Signal/belief rating row
// ============================================

function SignalRow({
  text,
  type,
  rating,
  onRatingChange,
}: {
  text: string;
  type: 'signal' | 'belief';
  rating: number | null;
  onRatingChange: (value: number) => void;
}) {
  return (
    <div className="signal-row">
      <span className={`signal-badge signal-badge-${type}`}>
        {type === 'signal' ? 'SIG' : 'BEL'}
      </span>
      <span className="signal-text">{text}</span>
      <div className="signal-buttons">
        <button
          className={`signal-btn signal-btn-up ${rating === 1 ? 'active' : ''}`}
          onClick={() => onRatingChange(rating === 1 ? 0 : 1)}
          title="Signal correct / pertinent pour le choix"
        >
          👍
        </button>
        <button
          className={`signal-btn signal-btn-down ${rating === -1 ? 'active' : ''}`}
          onClick={() => onRatingChange(rating === -1 ? 0 : -1)}
          title="Signal erroné / non pertinent"
        >
          👎
        </button>
      </div>
    </div>
  );
}

// ============================================
// ReasonRater component
// ============================================

export default function ReasonRater({ strategy, onChange, onSignalChange, taker, evaluatedSeat = 0, viewPosition = 0 }: ReasonRaterProps) {
  const sub = useCallback((text: string) =>
    substitutePlayerNames(text, taker ?? null, evaluatedSeat, viewPosition),
  [taker, evaluatedSeat, viewPosition]);
  const [ratings, setRatings] = useState<Map<string, number>>(new Map());
  const [corrections, setCorrections] = useState<Map<string, string>>(new Map());
  const [signalRatings, setSignalRatings] = useState<Map<string, number>>(new Map());

  if (!strategy) {
    return <div className="reason-rater-empty">Pas de données stratégiques disponibles.</div>;
  }

  const allReasons: Array<{ reason: Reason; isMain: boolean }> = [];
  if (strategy.raison_principale) {
    allReasons.push({
      reason: { ...strategy.raison_principale, texte: sub(strategy.raison_principale.texte) },
      isMain: true,
    });
  }
  if (strategy.raisons_secondaires) {
    for (const r of strategy.raisons_secondaires) {
      allReasons.push({ reason: { ...r, texte: sub(r.texte) }, isMain: false });
    }
  }

  const allSignals: Array<{ text: string; type: 'signal' | 'belief' }> = [];
  if (strategy.signaux_utilises) {
    for (const s of strategy.signaux_utilises) allSignals.push({ text: sub(s), type: 'signal' });
  }
  if (strategy.beliefs_utilises) {
    for (const b of strategy.beliefs_utilises) allSignals.push({ text: sub(b), type: 'belief' });
  }

  if (allReasons.length === 0 && allSignals.length === 0) {
    return <div className="reason-rater-empty">Aucune raison à évaluer.</div>;
  }

  const makeKey = (r: Reason, isMain: boolean) =>
    `${isMain ? 'main' : 'sec'}-${r.categorie}-${r.texte.slice(0, 30)}`;

  const makeSignalKey = (type: string, text: string) =>
    `${type}-${text.slice(0, 40)}`;

  const emitChange = (newRatings: Map<string, number>, newCorrections: Map<string, string>) => {
    const results: ReasonRatingResult[] = allReasons
      .filter(({ reason, isMain }) => newRatings.has(makeKey(reason, isMain)))
      .map(({ reason, isMain }) => {
        const key = makeKey(reason, isMain);
        return {
          reason_category: reason.categorie,
          reason_text: reason.texte,
          is_main_reason: isMain,
          rating: newRatings.get(key) ?? 0,
          expert_correction: newCorrections.get(key) || undefined,
        };
      });
    onChange(results);
  };

  const emitSignalChange = (newSignalRatings: Map<string, number>) => {
    if (!onSignalChange) return;
    const results: SignalRatingResult[] = allSignals
      .filter(({ type, text }) => newSignalRatings.has(makeSignalKey(type, text)))
      .map(({ type, text }) => ({
        signal_type: type,
        signal_text: text,
        rating: newSignalRatings.get(makeSignalKey(type, text)) ?? 0,
      }));
    onSignalChange(results);
  };

  const handleRatingChange = useCallback((key: string, value: number) => {
    setRatings(prev => {
      const next = new Map(prev);
      next.set(key, value);
      emitChange(next, corrections);
      return next;
    });
  }, [corrections, allReasons]);

  const handleCorrectionChange = useCallback((key: string, value: string) => {
    setCorrections(prev => {
      const next = new Map(prev);
      next.set(key, value);
      emitChange(ratings, next);
      return next;
    });
  }, [ratings, allReasons]);

  const handleSignalRatingChange = useCallback((key: string, value: number) => {
    setSignalRatings(prev => {
      const next = new Map(prev);
      if (value === 0) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      emitSignalChange(next);
      return next;
    });
  }, [allSignals, onSignalChange]);

  return (
    <div className="reason-rater">
      {allReasons.length > 0 && (
        <>
          <h5 className="reason-rater-title">Évaluation des raisons</h5>
          {allReasons.map(({ reason, isMain }) => {
            const key = makeKey(reason, isMain);
            return (
              <ReasonRow
                key={key}
                reason={reason}
                isMain={isMain}
                rating={ratings.get(key) ?? null}
                correction={corrections.get(key) ?? ''}
                onRatingChange={(v) => handleRatingChange(key, v)}
                onCorrectionChange={(v) => handleCorrectionChange(key, v)}
              />
            );
          })}
        </>
      )}

      {allSignals.length > 0 && (
        <div className="signal-rater-section">
          <h5 className="reason-rater-title">Signaux &amp; croyances utilisés</h5>
          {allSignals.map(({ text, type }) => {
            const key = makeSignalKey(type, text);
            return (
              <SignalRow
                key={key}
                text={text}
                type={type}
                rating={signalRatings.get(key) ?? null}
                onRatingChange={(v) => handleSignalRatingChange(key, v)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
